/* ==========================================================================
   Elora Visuals — interactie & scroll-beweging
   Native scrolling. GSAP + ScrollTrigger alleen waar het het verhaal versterkt.
   ========================================================================== */
(() => {
  'use strict';

  const html = document.documentElement;
  const hasGSAP = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  const hasSplit = typeof window.SplitText !== 'undefined';
  window.Elora = window.Elora || {};
  window.Elora.ready = true;

  if (!hasGSAP) html.classList.remove('motion');
  const motionOK = () => html.classList.contains('motion');

  if (hasGSAP) {
    gsap.registerPlugin(ScrollTrigger);
    if (hasSplit) gsap.registerPlugin(SplitText);
    ScrollTrigger.config({ ignoreMobileResize: true });
    gsap.defaults({ ease: 'power3.out' });
  }

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const clamp01 = (v) => clamp(v, 0, 1);
  const lerp = (a, b, t) => a + (b - a) * t;
  const desktop = () => window.matchMedia('(min-width: 990px)').matches;

  /* ------------------------------------------------------------------------
     Header — transparant over de hero, solide na scrollen, verbergt bij omlaag
     ------------------------------------------------------------------------ */
  const Header = {
    el: null,
    hero: null,
    overlay: null,
    lastY: 0,
    heroProgress: 0,

    init() {
      this.el = $('[data-header]');
      if (!this.el) return;
      const main = $('#MainContent');
      const first = main && main.firstElementChild;
      this.hero = first ? $('[data-header-overlay]', first) || (first.matches('[data-header-overlay]') ? first : null) : null;
      this.overlay = this.hero ? this.hero.dataset.headerOverlay : null;
      this.lastY = window.scrollY;
      this.update();
      if (!this.bound) {
        window.addEventListener('scroll', () => this.update(), { passive: true });
        window.addEventListener('resize', () => this.update(), { passive: true });
        this.bound = true;
      }
    },

    update() {
      if (!this.el) return;
      const y = window.scrollY;
      const vh = window.innerHeight;
      const inOverlay = this.hero && (this.hero.classList.contains('is-enhanced') ? this.heroProgress < 0.16 : y < vh * 0.5);
      this.el.classList.toggle('is-solid', !inOverlay && y > 8);
      this.el.classList.toggle('is-overlay-light', Boolean(inOverlay && this.overlay === 'light'));
      this.el.classList.toggle('is-overlay', Boolean(inOverlay));

      const menuOpen = html.classList.contains('menu-open');
      const heroEnd = this.hero ? this.hero.offsetTop + this.hero.offsetHeight - vh : 0;
      const threshold = Math.max(vh * 0.9, heroEnd + vh * 0.3);
      const delta = y - this.lastY;
      if (!menuOpen && y > threshold && delta > 6) this.el.classList.add('is-hidden');
      else if (delta < -6 || y <= threshold) this.el.classList.remove('is-hidden');
      this.lastY = y;
    },
  };

  /* ------------------------------------------------------------------------
     Mobiel menu
     ------------------------------------------------------------------------ */
  const Menu = {
    init() {
      const toggle = $('[data-menu-toggle]');
      const menu = $('[data-mobile-menu]');
      if (!toggle || !menu) return;
      menu.inert = true;

      const setOpen = (open) => {
        html.classList.toggle('menu-open', open);
        document.body.classList.toggle('is-locked', open);
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? toggle.dataset.labelClose : toggle.dataset.labelOpen);
        menu.setAttribute('aria-hidden', String(!open));
        menu.inert = !open;
        if (open) {
          Header.el && Header.el.classList.remove('is-hidden');
          setTimeout(() => { const first = $('a', menu); first && first.focus({ preventScroll: true }); }, 350);
        }
      };

      toggle.addEventListener('click', () => setOpen(!html.classList.contains('menu-open')));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && html.classList.contains('menu-open')) { setOpen(false); toggle.focus(); }
      });
      $$('a', menu).forEach((a) => a.addEventListener('click', () => setOpen(false)));
      window.matchMedia('(min-width: 990px)').addEventListener('change', (e) => { if (e.matches) setOpen(false); });
    },
  };

  /* ------------------------------------------------------------------------
     Lightbox — schermvullende portfolioweergave
     ------------------------------------------------------------------------ */
  const Lightbox = {
    dialog: null, items: [], index: 0,

    init() {
      this.dialog = $('[data-lightbox]');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.img = $('[data-lightbox-img]', this.dialog);
      this.caption = $('[data-lightbox-caption]', this.dialog);
      this.counter = $('[data-lightbox-counter]', this.dialog);

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-lightbox-item]');
        if (!trigger || !trigger.dataset.full) return;
        e.preventDefault();
        const group = trigger.dataset.lightboxItem;
        this.items = $$(`[data-lightbox-item="${group}"]`).filter((el) => el.dataset.full && el.offsetParent !== null);
        this.open(Math.max(0, this.items.indexOf(trigger)));
      });

      $('[data-lightbox-close]', this.dialog).addEventListener('click', () => this.close());
      $('[data-lightbox-prev]', this.dialog).addEventListener('click', () => this.go(-1));
      $('[data-lightbox-next]', this.dialog).addEventListener('click', () => this.go(1));
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.go(1);
        if (e.key === 'ArrowLeft') this.go(-1);
      });
      this.dialog.addEventListener('close', () => document.body.classList.remove('is-locked'));
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });

      // swipe
      const stage = $('[data-lightbox-stage]', this.dialog);
      let startX = null;
      stage.addEventListener('pointerdown', (e) => { startX = e.clientX; });
      stage.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 50) this.go(dx < 0 ? 1 : -1);
        startX = null;
      });
    },

    open(i) {
      document.body.classList.add('is-locked');
      this.dialog.showModal();
      this.show(i, 0);
    },

    close() { this.dialog.close(); },

    go(dir) {
      if (this.items.length < 2) return;
      this.show((this.index + dir + this.items.length) % this.items.length, dir);
    },

    show(i, dir) {
      this.index = i;
      const item = this.items[i];
      const swap = () => {
        this.img.src = item.dataset.full;
        this.img.alt = item.dataset.alt || '';
        this.caption.textContent = item.dataset.caption || '';
        const tpl = this.counter.dataset.template || '%c / %t';
        this.counter.textContent = tpl.replace('%c', i + 1).replace('%t', this.items.length);
      };
      if (hasGSAP && motionOK() && dir !== 0) {
        gsap.to(this.img, {
          opacity: 0, x: -24 * dir, duration: 0.25, ease: 'power2.in',
          onComplete: () => {
            swap();
            const reveal = () => gsap.fromTo(this.img, { opacity: 0, x: 24 * dir }, { opacity: 1, x: 0, duration: 0.6 });
            this.img.complete ? reveal() : this.img.addEventListener('load', reveal, { once: true });
          },
        });
      } else {
        swap();
      }
      // vooraf laden
      [1, -1].forEach((d) => {
        const n = this.items[(i + d + this.items.length) % this.items.length];
        if (n) { const pre = new Image(); pre.src = n.dataset.full; }
      });
    },
  };

  /* ------------------------------------------------------------------------
     Lookbook filters
     ------------------------------------------------------------------------ */
  const Lookbook = {
    init(root = document) {
      $$('[data-lookbook]', root).forEach((section) => {
        const buttons = $$('[data-filter]', section);
        const items = $$('.lookbook__item', section);
        buttons.forEach((btn) => btn.addEventListener('click', () => {
          // Nogmaals klikken op de actieve filter = weer alle foto's tonen
          const f = btn.classList.contains('is-active') ? 'all' : btn.dataset.filter;
          buttons.forEach((b) => { const on = b === btn && f !== 'all'; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on)); });
          section.classList.toggle('is-filtered', f !== 'all');
          const shown = [];
          items.forEach((item) => {
            const show = f === 'all' || item.dataset.category === f;
            item.classList.toggle('is-hidden', !show);
            if (show) shown.push(item);
          });
          if (hasGSAP) {
            if (motionOK()) {
              gsap.fromTo(shown, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, stagger: 0.05, clearProps: 'transform' });
              shown.forEach((item) => { const m = $('[data-reveal-image]', item); m && gsap.set(m, { clipPath: 'inset(0% 0% 0% 0%)' }); });
            }
            ScrollTrigger.refresh();
          }
        }));
      });
    },
  };

  /* ------------------------------------------------------------------------
     Diensten — beeld wisselt mee met hover / scrollpositie
     ------------------------------------------------------------------------ */
  const Services = {
    init(root = document) {
      $$('[data-services]', root).forEach((section) => {
        const items = $$('[data-service-item]', section);
        const slides = $$('[data-service-slide]', section);
        const current = $('[data-service-current]', section);
        let active = 0;
        let hovering = false;

        const set = (i) => {
          if (i === active) return;
          slides.forEach((s) => s.classList.remove('was-active'));
          slides[active] && slides[active].classList.add('was-active');
          slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
          items.forEach((it, n) => it.classList.toggle('is-active', n === i));
          if (current) current.textContent = String(i + 1).padStart(2, '0');
          active = i;
        };

        items.forEach((item, i) => {
          item.addEventListener('mouseenter', () => { hovering = true; set(i); });
          item.addEventListener('mouseleave', () => { hovering = false; });
          item.addEventListener('focusin', () => set(i));
        });

        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => {
            if (hovering || !desktop()) return;
            entries.forEach((entry) => { if (entry.isIntersecting) set(items.indexOf(entry.target)); });
          }, { rootMargin: '-45% 0px -45% 0px' });
          items.forEach((it) => io.observe(it));
        }
      });
    },
  };

  /* ------------------------------------------------------------------------
     Cinematic hero — scroll-gekoppelde schaal van schermvullend naar kader
     ------------------------------------------------------------------------ */
  const Hero = {
    trigger: null,
    timeline: null,

    // Ruimt de vorige hero op (nodig wanneer de Theme Editor de sectie opnieuw rendert)
    destroy() {
      if (this.trigger) { this.trigger.kill(); this.trigger = null; }
      if (this.timeline) { this.timeline.kill(); this.timeline = null; }
      Header.heroProgress = 0;
    },

    init(root = document) {
      const hero = root.matches && root.matches('[data-hero]') ? root : $('[data-hero]', root);
      if (!hero) return;
      this.destroy();
      const sticky = $('.hero__sticky', hero);
      const stage = $('[data-hero-stage]', hero);
      const media = $('[data-hero-media]', hero);
      const loadEl = $('[data-hero-load]', hero);
      const intro = $('[data-hero-intro]', hero);
      const shade = $('[data-hero-shade]', hero);
      const end = $('[data-hero-end]', hero);
      const scrollHint = $('[data-hero-scroll]', hero);

      if (!hasGSAP || !motionOK()) return;

      // Intro-animatie van de foto. Titel en slogan komen op via CSS (elora.css),
      // zodat ze nooit van JavaScript afhankelijk zijn om zichtbaar te worden.
      const tl = gsap.timeline({ delay: 0.1 });
      tl.fromTo(loadEl, { scale: 1.12, opacity: 0 }, { scale: 1, opacity: 1, duration: 2, ease: 'expo.out', clearProps: 'opacity' }, 0);
      if (scrollHint) tl.fromTo(scrollHint, { opacity: 0 }, { opacity: 1, duration: 1, clearProps: 'opacity' }, 1.2);
      this.timeline = tl;

      // Scroll-gekoppelde schaal
      const ratio = parseFloat(media.dataset.ratio) || 0.75;
      const focal = clamp01((parseFloat(media.dataset.focal) || 50) / 100);
      hero.classList.add('is-enhanced');
      hero.style.setProperty('--hero-ratio', ratio);

      let g = null;
      const measure = () => {
        const W = document.documentElement.clientWidth;
        const H = sticky.clientHeight;
        const h = W / ratio;
        media.style.width = `${W}px`;
        const mobile = W < 750;
        const s0 = Math.max(1, H / h);
        const x0 = (W - W * s0) / 2;
        const y0 = clamp(H / 2 - focal * h * s0, H - h * s0, 0);
        const maxW = mobile ? W * 0.72 : Math.min(W * 0.32, 600);
        const maxH = mobile ? H * 0.54 : H * 0.7;
        const s1 = Math.min(maxW / W, maxH / h);
        const bw = W * s1, bh = h * s1;
        const cy = mobile ? H * 0.42 : H * 0.5 + 12;
        g = { W, H, s0, x0, y0, s1, x1: (W - bw) / 2, y1: cy - bh / 2, bw, bh };
      };

      const ease = gsap.parseEase('power2.inOut');
      const render = (p) => {
        if (!g) return;
        const e = ease(clamp01((p - 0.04) / 0.78));
        const s = lerp(g.s0, g.s1, e);
        const x = lerp(g.x0, g.x1, e);
        const y = lerp(g.y0, g.y1, e);
        media.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s})`;
        const top = lerp(0, g.y1, e);
        const left = lerp(0, g.x1, e);
        const right = lerp(0, g.W - g.x1 - g.bw, e);
        const bottom = lerp(0, g.H - g.y1 - g.bh, e);
        stage.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px)`;

        const introOut = clamp01(p / 0.2);
        if (intro) intro.style.opacity = String(1 - introOut);
        if (intro) intro.style.transform = `translate3d(0, ${-50 * introOut}px, 0)`;
        if (shade) shade.style.opacity = String(1 - clamp01(p / 0.35));
        if (scrollHint) scrollHint.style.visibility = p > 0.08 ? 'hidden' : '';

        if (end) {
          const endIn = clamp01((p - 0.7) / 0.2);
          end.style.opacity = String(endIn);
          end.style.visibility = endIn > 0 ? 'visible' : 'hidden';
          end.style.transform = `translate3d(0, ${24 * (1 - endIn)}px, 0)`;
        }

        Header.heroProgress = p;
        Header.update();
      };

      const state = { p: 0 };
      measure();
      render(0);
      // Nieuwe foto geladen (bijv. vervangen in de Theme Editor): afmetingen opnieuw meten
      const img = $('img', media);
      if (img && !img.complete) img.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });

      this.trigger = ScrollTrigger.create({
        trigger: hero,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self) => gsap.to(state, { p: self.progress, duration: 0.6, ease: 'power3.out', overwrite: true, onUpdate: () => render(state.p) }),
        onRefresh: (self) => { measure(); state.p = self.progress; render(state.p); },
      });
    },
  };

  /* ------------------------------------------------------------------------
     Onthullingen, tekstovergangen en parallax
     ------------------------------------------------------------------------ */
  const Reveal = {
    init(root = document) {
      if (!hasGSAP || !motionOK()) return;

      // Kopregels per regel onthullen
      const splitTargets = $$('[data-split-lines]', root).filter((el) => !el.classList.contains('is-split'));
      const doSplit = () => splitTargets.forEach((el) => {
        const immediate = el.hasAttribute('data-immediate');
        if (!hasSplit) {
          el.classList.add('is-split');
          gsap.fromTo(el, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1.2, delay: immediate ? 0.3 : 0, scrollTrigger: immediate ? null : { trigger: el, start: 'top 86%', once: true } });
          return;
        }
        SplitText.create(el, {
          type: 'lines',
          mask: 'lines',
          linesClass: 'split-line',
          autoSplit: true,
          onSplit(self) {
            el.classList.add('is-split');
            return gsap.from(self.lines, {
              yPercent: 112, duration: 1.3, ease: 'expo.out', stagger: 0.1,
              delay: immediate ? 0.35 : 0,
              scrollTrigger: immediate ? null : { trigger: el, start: 'top 86%', once: true },
            });
          },
        });
      });
      document.fonts && document.fonts.status !== 'loaded' ? document.fonts.ready.then(doSplit) : doSplit();

      // Tekst zacht laten opkomen
      const texts = $$('[data-reveal-text]', root);
      ScrollTrigger.batch(texts, {
        start: 'top 92%',
        once: true,
        onEnter: (batch) => gsap.to(batch, { opacity: 1, y: 0, duration: 1.1, stagger: 0.08, overwrite: true }),
      });

      // Beelden onthullen via een verticaal masker
      const images = $$('[data-reveal-image]', root);
      gsap.set(images, { clipPath: 'inset(100% 0% 0% 0%)' });
      const revealImages = (batch) => {
        gsap.to(batch, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, ease: 'expo.inOut', stagger: 0.14, overwrite: true });
        batch.forEach((el, i) => {
          const img = $('img', el);
          if (img) gsap.fromTo(img, { scale: 1.22 }, { scale: 1, duration: 2, ease: 'expo.out', delay: i * 0.14, clearProps: 'transform' });
        });
      };
      const immediate = images.filter((el) => el.hasAttribute('data-immediate'));
      if (immediate.length) gsap.delayedCall(0.25, () => revealImages(immediate));
      ScrollTrigger.batch(images.filter((el) => !el.hasAttribute('data-immediate')), { start: 'top 88%', once: true, onEnter: revealImages });

      // Subtiele parallax
      const mm = gsap.matchMedia();
      mm.add({ isDesktop: '(min-width: 750px)', isMobile: '(max-width: 749px)' }, (ctx) => {
        const factor = ctx.conditions.isMobile ? 0.6 : 1;
        $$('[data-parallax]', root).forEach((frame) => {
          const amount = { subtle: 4.5, medium: 7 }[frame.dataset.parallax];
          const target = $('[data-parallax-target]', frame);
          if (!amount || !target) return;
          gsap.fromTo(target, { yPercent: -amount * factor }, {
            yPercent: amount * factor, ease: 'none',
            scrollTrigger: { trigger: frame, start: 'top bottom', end: 'bottom top', scrub: true },
          });
        });
      });

      // Diepte: elke kolom beweegt met een eigen snelheid
      mm.add('(min-width: 750px)', () => {
        $$('[data-work-columns]', root).forEach((section) => {
          $$('[data-depth]', section).forEach((col) => {
            const d = parseFloat(col.dataset.depth) || 0;
            gsap.fromTo(col, { y: () => d * window.innerHeight / 100 }, {
              y: () => -d * window.innerHeight / 100, ease: 'none',
              scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
            });
          });
        });
      });

      // Afsluitend beeld opent zich van kader naar schermbreed
      $$('[data-closing]', root).forEach((section) => {
        const frame = $('[data-closing-frame]', section);
        const media = $('[data-closing-media]', section);
        const content = $('[data-closing-content]', section);
        const tl = gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 95%', end: 'top 15%', scrub: 0.6 } });
        tl.fromTo(frame, { clipPath: 'inset(9% 16% 9% 16%)' }, { clipPath: 'inset(0% 0% 0% 0%)', ease: 'none' }, 0)
          .fromTo(media, { scale: 1.16 }, { scale: 1, ease: 'none' }, 0);
        if (content) gsap.fromTo(content, { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 1.2, scrollTrigger: { trigger: section, start: 'top 35%', once: true } });
      });

      // Zachte overgang naar het laatste deel
      $$('[data-cta]', root).forEach((cta) => {
        const heading = $('.cta__heading', cta);
        if (heading && desktop()) {
          gsap.fromTo(heading, { xPercent: 4 }, { xPercent: 0, ease: 'none', scrollTrigger: { trigger: cta, start: 'top bottom', end: 'center center', scrub: true } });
        }
      });
    },
  };

  /* ------------------------------------------------------------------------
     Uitgelicht portfolio — horizontaal op desktop, native swipe op mobiel
     ------------------------------------------------------------------------ */
  const Featured = {
    init(root = document) {
      $$('[data-featured]', root).forEach((section) => {
        const pin = $('[data-featured-pin]', section);
        const track = $('[data-featured-track]', section);
        const bar = $('[data-featured-progress]', section);
        const setProgress = (p) => bar && bar.style.setProperty('--progress', p.toFixed(4));

        // Native scroll (mobiel of zonder beweging)
        track.addEventListener('scroll', () => {
          if (section.classList.contains('is-horizontal')) return;
          const max = track.scrollWidth - track.clientWidth;
          setProgress(max > 0 ? track.scrollLeft / max : 0);
        }, { passive: true });

        if (!hasGSAP || !motionOK()) return;

        const mm = gsap.matchMedia();
        mm.add('(min-width: 990px)', () => {
          section.classList.add('is-horizontal');
          const distance = () => Math.max(0, track.scrollWidth - document.documentElement.clientWidth);
          const tween = gsap.to(track, {
            x: () => -distance(),
            ease: 'none',
            scrollTrigger: {
              trigger: pin,
              start: 'top top',
              end: () => `+=${distance()}`,
              pin: true,
              scrub: 0.8,
              invalidateOnRefresh: true,
              onUpdate: (self) => setProgress(self.progress),
            },
          });
          $$('[data-featured-img]', section).forEach((img) => {
            gsap.fromTo(img, { xPercent: 4 }, {
              xPercent: -4, ease: 'none',
              scrollTrigger: { trigger: img.parentElement, containerAnimation: tween, start: 'left right', end: 'right left', scrub: true },
            });
          });
          return () => { section.classList.remove('is-horizontal'); gsap.set(track, { clearProps: 'transform' }); };
        });
      });
    },
  };

  /* ------------------------------------------------------------------------
     Overig
     ------------------------------------------------------------------------ */
  const Misc = {
    init() {
      const status = $('[data-autofocus]');
      if (status) { status.focus({ preventScroll: true }); status.scrollIntoView({ block: 'center' }); }

      $$('[data-back-to-top]').forEach((a) => a.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: motionOK() ? 'smooth' : 'auto' });
        const main = $('#MainContent'); main && main.focus({ preventScroll: true });
      }));
    },
  };

  /* ------------------------------------------------------------------------
     Start
     ------------------------------------------------------------------------ */
  const start = () => {
    Header.init();
    Menu.init();
    Lightbox.init();
    Lookbook.init();
    Services.init();
    Hero.init();
    Featured.init();
    Reveal.init();
    Misc.init();
    if (hasGSAP) {
      window.addEventListener('load', () => ScrollTrigger.refresh());
      document.fonts && document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Shopify theme editor: secties opnieuw initialiseren na bewerken
  document.addEventListener('shopify:section:unload', (e) => {
    if ($('[data-hero]', e.target)) Hero.destroy();
  });
  document.addEventListener('shopify:section:load', (e) => {
    if ($('[data-hero]', e.target)) { Header.init(); Hero.init(e.target); }
    Lookbook.init(e.target);
    Services.init(e.target);
    Featured.init(e.target);
    Reveal.init(e.target);
    if (hasGSAP) ScrollTrigger.refresh();
  });
})();
